import CloseIcon from "@mui/icons-material/Close";
import ExpandIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  ButtonBase,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext.tsx";
import { GUIDE_WIDTH } from "../caseLayout.ts";
import { SectionLabel } from "../general/SectionLabel.tsx";
import { Guide, useGuide } from "./GuideContext.tsx";
import { MarkerNumber } from "./GuideMarker.tsx";
import { GUIDE_PAGES, GuidePageId } from "./guidePages.ts";

/** How strongly the open entry is washed with the primary colour. */
const OPEN_ENTRY_ALPHA = 0.03;

/** One part of an open entry: what you enter, or what is assumed. */
const EntryPart = ({ label, text }: { label: string; text: string }) => (
  <Box>
    <SectionLabel>{label}</SectionLabel>
    <Typography variant="body2" sx={{ mt: 0.25 }}>
      {text}
    </Typography>
  </Box>
);

/** The entry of an element of the page, opened from its marker or its header. */
const GuideEntry = ({
  guide: { picked, pointed, dispatch },
  page,
  entry,
  number,
}: {
  guide: Guide;
  page: GuidePageId;
  entry: string;
  number: number;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, {
    keyPrefix: `guide.pages.${page}.entries.${entry}`,
  });
  const { t: tGuide } = useTranslation(undefined, { keyPrefix: "guide" });
  const isPicked = picked === entry;
  const isPointed = pointed === entry;
  const example = t("example", { defaultValue: "" });

  // a marker far down the page leads to an entry that may be out of view
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isPicked) card.current?.scrollIntoView?.({ block: "nearest" });
  }, [isPicked]);

  const point = (pointedAt?: string) =>
    dispatch({ type: "point", entry: pointedAt });
  return (
    <Paper
      ref={card}
      id={`guide-${entry}`}
      variant="outlined"
      onMouseEnter={() => point(entry)}
      onMouseLeave={() => point()}
      sx={{
        flex: "none",
        borderRadius: 2.5,
        overflow: "hidden",
        borderColor:
          isPicked || isPointed
            ? alpha(theme.palette.primary.main, isPicked ? 0.45 : 0.25)
            : undefined,
        bgcolor: isPicked
          ? alpha(theme.palette.primary.main, OPEN_ENTRY_ALPHA)
          : undefined,
      }}
    >
      <ButtonBase
        aria-expanded={isPicked}
        onClick={() => dispatch({ type: "toggleEntry", page, entry })}
        onFocus={() => point(entry)}
        onBlur={() => point()}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          gap: 1.25,
          px: 1.75,
          py: 1.25,
          textAlign: "left",
        }}
      >
        <MarkerNumber picked={isPicked} pointed={isPointed}>
          {number}
        </MarkerNumber>
        <Typography variant="body2" sx={{ flex: 1, fontWeight: 700 }}>
          {t("title")}
        </Typography>
        <ExpandIcon
          fontSize="small"
          sx={{
            color: "text.secondary",
            transform: isPicked ? "rotate(180deg)" : undefined,
          }}
        />
      </ButtonBase>
      {isPicked && (
        <Stack spacing={1.25} sx={{ pl: 5.75, pr: 1.75, pb: 1.75 }}>
          <EntryPart label={tGuide("youEnter")} text={t("enter")} />
          <EntryPart label={tGuide("assumes")} text={t("assumes")} />
          {example !== "" && (
            <Box
              sx={{
                bgcolor: "action.hover",
                borderRadius: 2,
                px: 1.5,
                py: 1.25,
              }}
            >
              <EntryPart label={tGuide("example")} text={example} />
            </Box>
          )}
        </Stack>
      )}
    </Paper>
  );
};

/** An entry about the page, or the case, as a whole. */
const GeneralEntry = ({ title, text }: { title: string; text: string }) => (
  <Paper
    variant="outlined"
    sx={{ flex: "none", borderRadius: 2.5, px: 1.75, py: 1.25 }}
  >
    <Typography variant="body2" sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.25 }}>
      {text}
    </Typography>
  </Paper>
);

/** What holds for the whole case, the same on every page: folded until asked for. */
const CaseWide = () => {
  const { t } = useTranslation(undefined, { keyPrefix: "guide.caseWide" });
  const [open, setOpen] = useState(false);
  const entries = t("entries", { returnObjects: true }) as {
    [key: string]: { title: string; text: string };
  };

  return (
    <>
      <ButtonBase
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        sx={{
          flex: "none",
          justifyContent: "space-between",
          mt: 1,
          py: 0.5,
          color: "primary.main",
          typography: "body2",
          fontWeight: 600,
          textAlign: "left",
        }}
      >
        {t("title")}
        <ExpandIcon
          fontSize="small"
          sx={{ transform: open ? "rotate(180deg)" : undefined }}
        />
      </ButtonBase>
      {open &&
        Object.entries(entries).map(([key, { title, text }]) => (
          <GeneralEntry key={key} title={title} text={text} />
        ))}
    </>
  );
};

/**
 * The guide of the open page: beside the page, or over its right side where
 * the page would be squeezed, always under the app bar and with no backdrop,
 * so that the markers stay within reach. Its contents stay in view while the
 * page scrolls.
 */
export const GuidePanel = () => {
  const guide = useGuide();
  const { theme } = useTheme();
  const { t } = useTranslation();
  if (guide?.page === undefined || !guide.shown) return null;
  const { page, overPage, dispatch, entries } = guide;
  const { general } = GUIDE_PAGES[page];

  return (
    <Drawer
      variant="permanent"
      anchor="right"
      slotProps={{ paper: { elevation: 0 } }}
      sx={{
        flexShrink: 0,
        // over the page it takes up no room of the page's
        width: overPage ? 0 : GUIDE_WIDTH,
        "& .MuiDrawer-paper": {
          width: `min(${GUIDE_WIDTH}px, 100%)`,
          boxShadow: overPage
            ? `-12px 0 32px ${alpha(theme.palette.primary.main, 0.18)}`
            : undefined,
        },
      }}
    >
      {/* the guide starts under the app bar, which lies over it */}
      <Toolbar sx={{ flex: "none" }} />
      <Box
        component="aside"
        id="guide"
        aria-label={t("guide.title")}
        sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            flex: "none",
            alignItems: "flex-start",
            justifyContent: "space-between",
            px: 2.75,
            pt: 2.25,
            pb: 1.75,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box>
            <SectionLabel>
              {t(`case.details.section.configuration.${page}`)}
            </SectionLabel>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
              {t("guide.title")}
            </Typography>
          </Box>
          <IconButton
            aria-label={t("guide.close")}
            onClick={() => dispatch({ type: "toggle" })}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack
          spacing={0.75}
          sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2.75, py: 2 }}
        >
          <Typography
            variant="body2"
            sx={{ flex: "none", color: "text.secondary", mb: 1 }}
          >
            {t("guide.intro")}
          </Typography>
          {entries.map((entry, index) => (
            <GuideEntry
              key={entry}
              guide={guide}
              page={page}
              entry={entry}
              number={index + 1}
            />
          ))}
          <Box sx={{ flex: "none", mt: 1.5, pb: 0.25 }}>
            <SectionLabel>{t("guide.throughout")}</SectionLabel>
          </Box>
          {general.map((entry) => (
            <GeneralEntry
              key={entry}
              title={t(`guide.pages.${page}.general.${entry}.title`)}
              text={t(`guide.pages.${page}.general.${entry}.text`)}
            />
          ))}
          <CaseWide />
        </Stack>
      </Box>
    </Drawer>
  );
};
